import type { PageContent } from "./problems";

export const industries: PageContent[] = [
  {
    slug: "small-midsize-businesses",
    videoEmbedUrl: "https://play.gumlet.io/embed/6ad30ee2085713f348b0ad84",
    videoEmbedUrlMobile: "https://play.gumlet.io/embed/6ad31469085713f348b0c334",
    title: "Leadership Training for Small and Midsize Businesses | Leadership Edge Live",
    metaDescription:
      "Practical leadership training for growing companies with no training department. Online courses, 30-minutes sessions, for promoted-from-within managers.",
    eyebrow: "SMALL AND MIDSIZE BUSINESSES",
    headline: "Your first HR hire can't fix your managers",
    supporting: "You grew from 40 people to 300. Nobody taught the supervisors how to supervise.",
    primaryCTA: {
      label: "View the Course Catalog",
      to: "/course-catalog/",
    },
    secondaryCTA: {
      label: "Talk to a Development Pro",
      to: "/contact/",
    },
    trustBar: "ONLINE, 30-MINUTES SESSIONS. NO TRAVEL. NO CONSULTANTS.",
    symptomsEyebrow: "What this looks like",
    symptomsTitle: "The owner is still the escalation path",
    symptoms: [
      {
        title: "Everyone wears every hat",
        body: "With limited staff, everyone must juggle multiple roles; often stretching focus, time, and expertise.",
      },
      {
        title: "Everything routes back to the owner",
        body: "Supervisors bring problems, not decisions. You are stretched too thin.",
      },
      {
        title: "HR is one person, part-time",
        body: "Your one HR hire is buried in payroll. Coaching waits.",
      },
      {
        title: "Good people quit for small reasons",
        body: "Not pay. Nobody ever told them where they stood.",
      },
    ],
    impactTitle: "What it costs you",
    impactBullets: [
      "Replacing one employee: half their salary",
      "You work 24x7 on tasks that you don't love",
      "First-year turnover, again and again",
      "Growth stalls; nobody can run teams",
    ],
    evidence: {
      quote:
        "The cost of replacing an individual employee can range from one-half to two times the employee's annual salary, and that's a conservative estimate.",
      source: "Gallup, 2019",
      href: "https://www.gallup.com/workplace/247391/fixable-problem-costs-businesses-trillion.aspx",
    },
    approachEyebrow: "How it works",
    approachTitle: "Small doses, real work, no travel",
    approachSteps: [
      {
        title: "Find the gaps",
        body: "A short assessment shows what people need.",
      },
      {
        title: "Start with one course",
        body: "Pick a course. 30-minutes a week, online.",
      },
      {
        title: "Add coaching for more impact",
        body: "Bring a real challenge and your coach will help you solve it.",
      },
      {
        title: "Continue to learn",
        body: "Stack courses over a year, not one offsite.",
      },
    ],
    behaviorsTitle: "Where we usually start",
    behaviors: [
      {
        name: "Handling the hard conversation",
        body: "Say it early, before it becomes a resignation.",
        courses: "Conquering Conflict, On-the-Spot Development Conversations",
      },
      {
        name: "Letting go of the work",
        body: "Hand work over. Stop being the bottleneck.",
        courses: "Delegating for Development",
      },
      {
        name: "Setting expectations that stick",
        body: "People know what good looks like.",
        courses: "Performance Management, Driving Results Through Goal Setting",
      },
      {
        name: "Building trust fast",
        body: "New supervisors earn credibility with their old crew.",
        courses: "Creating a Culture of Trust, New Leader Boot Camp",
      },
    ],
    finalTitle: "Start with one supervisor and one problem",
    finalBody: "Tell us what's breaking. We'll point you at one course and one place to start.",
    caseStudy: {
      label: "Illustrative scenario",
      company: "A 180-person building products distributor",
      situation:
        "Grew from 60 to 180 in four years. Six supervisors, all promoted off the warehouse floor. The owner still settled every dispute.",
      action:
        "All six took New Leader Boot Camp, then Conquering Conflict. The owner and HR manager took Delegating for Development too.",
      result: [
        "Supervisors settle scheduling disputes without the owner",
        "Fewer first-year exits on the warehouse crew",
        "The owner's calendar opens up for customers",
      ],
    },
  },
  {
    slug: "manufacturing",
    videoEmbedUrl: "https://play.gumlet.io/embed/6ad30ee2c56c0515acab8903",
    videoEmbedUrlMobile: "https://play.gumlet.io/embed/6ad31469085713f348b0c335",
    title: "Leadership Training for Manufacturing Supervisors | Leadership Edge Live",
    metaDescription:
      "Frontline leadership training for plants. Online courses that fit around shifts and help shift leads coach crews, hold standards, and keep good operators.",
    eyebrow: "MANUFACTURING",
    headline: "Your best operator became your weakest supervisor overnight",
    supporting: "Plants promote for skill, then hope for leadership. Good people walk.",
    primaryCTA: {
      label: "View the Course Catalog",
      to: "/course-catalog/",
    },
    secondaryCTA: {
      label: "Talk to a Development Pro",
      to: "/contact/",
    },
    trustBar: "ONLINE, 30-MINUTES SESSIONS. Fits around shifts without shutting the line down.",
    symptomsEyebrow: "On the floor",
    symptomsTitle: "Where production and people problems overlap",
    symptoms: [
      {
        title: "Handoffs between shifts go sideways",
        body: "Second shift inherits a mess. Nobody says so.",
      },
      {
        title: "Supervisors coach by pointing at numbers",
        body: "Scrap gets reviewed. The operator never hears what to change.",
      },
      {
        title: "Nobody addresses the safety shortcut",
        body: "Someone skips a step. The lead saw it, said nothing.",
      },
      {
        title: "New hires quit in month two",
        body: "They got a badge and a machine, never a conversation.",
      },
    ],
    impactTitle: "What that costs a plant",
    impactBullets: [
      "Scrap and rework traced to nobody",
      "Overtime covering for people who quit",
      "Unaddressed skill gaps",
      "Safety and production opportunities are spotted but never realized",
    ],
    evidence: {
      quote:
        "The net need for new employees in U.S. manufacturing could be around 3.8 million between 2024 and 2033, and around half of those jobs could go unfilled.",
      source: "Deloitte and The Manufacturing Institute, Taking Charge, 2024",
      href: "https://www.deloitte.com/us/en/insights/industry/manufacturing-industrial-products/supporting-us-manufacturing-growth-amid-workforce-challenges.html",
    },
    approachEyebrow: "How it works",
    approachTitle: "Training that fits around the schedule",
    approachSteps: [
      {
        title: "Assess your leads",
        body: "A short assessment shows who struggles with what.",
      },
      {
        title: "30-minutes; once per week",
        body: "Online sessions between shifts. No shutdown, no travel.",
      },
      {
        title: "Practice on real challenges",
        body: "Focus on real challenges and apply the learning.",
      },
      {
        title: "Build a bench",
        body: "Stack courses so people are ready before retirements.",
      },
    ],
    behaviorsTitle: "What we work on",
    behaviors: [
      {
        name: "Feedback in the moment",
        body: "Correct the shortcut when you see it.",
        courses: "On-the-Spot Development Conversations, Conquering Conflict",
      },
      {
        name: "Running a real huddle",
        body: "Five minutes that change the line.",
        courses: "Strengthening Teams Through Communication",
      },
      {
        name: "Speaking up about risk",
        body: "Crews report near misses instead of hiding them.",
        courses: "Psychological Safety",
      },
      {
        name: "Holding standards without heat",
        body: "Clear expectations, followed up. Discipline isn't a surprise.",
        courses: "Performance Management",
      },
    ],
    finalTitle: "Start with one shift",
    finalBody:
      "Tell us where crews are struggling. We'll suggest one course and one group of leads.",
    caseStudy: {
      label: "Illustrative scenario",
      company: "A 240-person metal fabricator",
      situation:
        "Three shifts, eleven supervisors, nine promoted off the floor. Second shift turnover ran double first shift's, and handoff notes had stopped.",
      action:
        "All eleven ran New Leader Boot Camp, then Strengthening Teams Through Communication. Psychological Safety followed an unreported near miss.",
      result: [
        "Shift handoffs happen again, in writing",
        "Near misses reported instead of quietly worked around",
        "Fewer second-shift resignations in the first ninety days",
      ],
    },
  },
  {
    slug: "supply-chain-logistics",
    videoEmbedUrl: "https://play.gumlet.io/embed/6ad30ee2085713f348b0ad88",
    title: "Leadership Training for Supply Chain and Logistics Teams | Leadership Edge Live",
    metaDescription:
      "Frontline leadership training for DCs, fleets, and 3PLs. Online courses that fit shift work and get area leads ready before peak season hits.",
    eyebrow: "SUPPLY CHAIN AND LOGISTICS",
    headline: "Demanding environments expose every weak supervisor you have",
    supporting: "Inbound's late, outbound's behind, and people manage with tension.",
    primaryCTA: {
      label: "View the Course Catalog",
      to: "/course-catalog/",
    },
    secondaryCTA: {
      label: "Talk to a Development Pro",
      to: "/contact/",
    },
    trustBar: "ONLINE, 30-MINUTES SESSIONS. WORKS ACROSS SHIFTS AND SITES.",
    symptomsEyebrow: "On the dock",
    symptomsTitle: "The problems that surface at peak",
    symptoms: [
      {
        title: "Seasonal hires disappear by week three",
        body: "You onboard 60 pickers. Half are gone by Thanksgiving.",
      },
      {
        title: "Leads chase metrics, not people",
        body: "Units per hour gets reviewed daily. Nobody asks why.",
      },
      {
        title: "Drivers only hear from dispatch when something breaks",
        body: "No check-in, no coaching. Then they take another job.",
      },
      {
        title: "Site leads don't talk to each other",
        body: "Two DCs, two ways of everything. Nobody wanted the conflict.",
      },
    ],
    impactTitle: "What it costs your operation",
    impactBullets: [
      "Rehiring the same seasonal roles twice",
      "Overtime covering shifts nobody wants",
      "Missed cutoffs that customers actually notice",
      "Safety incidents and tension during the busiest weeks",
    ],
    evidence: {
      quote:
        "Transportation, warehousing and utilities recorded 3.489 million separations in 2025 on roughly 6.6 million jobs, the highest separations-to-employment ratio of any goods-adjacent sector tracked.",
      source: "U.S. Bureau of Labor Statistics, JOLTS Table 19, 2025 annual data",
      href: "https://www.bls.gov/news.release/jolts.t19.htm",
    },
    approachEyebrow: "How it works",
    approachTitle: "Built for shift work and peak",
    approachSteps: [
      {
        title: "See the gaps by site",
        body: "A short assessment per site shows who needs what.",
      },
      {
        title: "30-minutes a week",
        body: "Short enough to maintain the operation.",
      },
      {
        title: "Practice before peak",
        body: "Run courses in slow months, before volume triples.",
      },
      {
        title: "Same language across sites",
        body: "Every lead learns the same model. Transfers aren't resets.",
      },
    ],
    behaviorsTitle: "What we work on",
    behaviors: [
      {
        name: "Onboarding that sticks",
        body: "New pickers get a real first week.",
        courses: "Building Employee Engagement",
      },
      {
        name: "Coaching the number",
        body: "Talk to the person behind the UPH.",
        courses: "Collaborative Coaching Conversations",
      },
      {
        name: "Handling conflict on the dock",
        body: "Two leads, one bottleneck, sorted without the GM.",
        courses: "Conquering Conflict",
      },
      {
        name: "Leading people you rarely see",
        body: "Drivers and night shift need a different check-in.",
        courses: "Leading Remote Teams",
      },
    ],
    finalTitle: "Get ready before the ramp",
    finalBody: "Tell us your peak calendar. We'll pick one course and a start date.",
    caseStudy: {
      label: "Illustrative scenario",
      company: "A regional 3PL running three distribution centers",
      situation:
        "Four hundred employees at peak, half seasonal. Area leads were promoted pickers with no training, and thirty-day turnover kept climbing.",
      action:
        "Fourteen area leads took New Leader Boot Camp in spring, then Collaborative Coaching Conversations before the ramp. Site managers took Conquering Conflict.",
      result: [
        "More seasonal hires made it past thirty days",
        "Leads sorted dock disputes without the GM",
        "Fewer cutoffs missed during the fall ramp",
      ],
    },
  },
  {
    slug: "healthcare",
    videoEmbedUrl: "https://play.gumlet.io/embed/6ad30ee2a55cdea9c48012e8",
    videoEmbedUrlMobile: "https://play.gumlet.io/embed/6ad31469a55cdea9c480284a",
    title:
      "Leadership Training for Charge Nurses and Health System Managers | Leadership Edge Live",
    metaDescription:
      "Leadership training for hospitals and health systems. Online courses that fit 12-hour shifts and help charge nurses lead, coach, and keep first-year staff.",
    eyebrow: "HEALTHCARE",
    headline: "Your charge nurses were never taught to lead",
    supporting: "They were great at the bedside, so you gave them a unit.",
    primaryCTA: {
      label: "View the Course Catalog",
      to: "/course-catalog/",
    },
    secondaryCTA: {
      label: "Talk to a Development Pro",
      to: "/contact/",
    },
    trustBar: "ONLINE, 30-MINUTES SESSIONS. Fits 12-hour shifts and self-scheduling.",
    symptomsEyebrow: "On the unit",
    symptomsTitle: "What shows up in exit interviews",
    symptoms: [
      {
        title: "New grads leave inside a year",
        body: "The residency program is solid. Nobody asked how they're doing.",
      },
      {
        title: "Callouts get handled by guilt",
        body: "Filling gaps runs on favors. The same three burn out.",
      },
      {
        title: "Nobody addresses the difficult colleague",
        body: "Everyone knows who it is. Leadership hopes they retire first.",
      },
      {
        title: "Managers escalate to HR too fast",
        body: "Small friction becomes an investigation. Nobody felt equipped.",
      },
    ],
    impactTitle: "What turnover really costs",
    impactBullets: [
      "Major gaps in your staff",
      "New hire orientation paid twice in one year",
      "Tension and miscommunication on the floor",
      "Patient experience scores tracking staffing",
    ],
    evidence: {
      quote:
        "The average cost of turnover for one staff RN is $61,110, and it cost the average hospital $4.75 million last year. Each 1% change in RN turnover is worth $289,000.",
      source: "NSI Nursing Solutions, 2025 National Health Care Retention & RN Staffing Report",
      href: "https://www.nsinursingsolutions.com/Documents/Library/NSI_National_Health_Care_Retention_Report.pdf",
    },
    approachEyebrow: "How it works",
    approachTitle: "Leadership training that fits clinical schedules",
    approachSteps: [
      {
        title: "Find the gap",
        body: "A short assessment shows who needs what.",
      },
      {
        title: "30-minutes; once per week",
        body: "So a 12-hour shift doesn't derail anyone.",
      },
      {
        title: "Practice real unit problems",
        body: "Charge nurses bring the conversation they've been avoiding.",
      },
      {
        title: "Grow the next bench",
        body: "Develop the nurses who could take a unit.",
      },
    ],
    behaviorsTitle: "What we work on",
    behaviors: [
      {
        name: "The conversation nobody's having",
        body: "Address it before it becomes an HR file.",
        courses: "Conquering Conflict, On-the-Spot Development Conversations",
      },
      {
        name: "Speaking up safely",
        body: "Staff raise a near miss without weighing politics.",
        courses: "Psychological Safety",
      },
      {
        name: "Checking in on purpose",
        body: "A two-minute question that keeps a new grad.",
        courses: "Collaborative Coaching Conversations",
      },
      {
        name: "Leading through constant change",
        body: "New EMR, new policy, new ratios. Managers explain.",
        courses: "Leading Change",
      },
    ],
    finalTitle: "Start with one unit",
    finalBody: "Tell us which units are losing people. We'll suggest one course and one cohort.",
    caseStudy: {
      label: "Illustrative scenario",
      company: "A 400-bed community hospital",
      situation:
        "Med-surg first-year RN turnover led the system. Charge nurses were strong clinically and untrained in feedback or conflict.",
      action:
        "Two cohorts completed New Leader Boot Camp and Psychological Safety. Unit managers added Collaborative Coaching Conversations and a monthly check-in.",
      result: [
        "More first-year nurses staying past orientation",
        "Fewer unit conflicts escalating to HR",
        "Callouts covered without the favor economy",
      ],
    },
  },
  {
    slug: "construction-engineering",
    videoEmbedUrl: "https://play.gumlet.io/embed/6ad30ee2085713f348b0ad85",
    title: "Leadership Training for Foremen and Project Managers | Leadership Edge Live",
    metaDescription:
      "Leadership training for contractors and engineering firms. Online courses that fit jobsite hours and stop field-office friction before it hits the job.",
    eyebrow: "CONSTRUCTION AND ENGINEERING",
    headline: "Your foremen learned to lead by being yelled at",
    supporting: "That's how the trade passed it down. It doesn't work anymore.",
    primaryCTA: {
      label: "View the Course Catalog",
      to: "/course-catalog/",
    },
    secondaryCTA: {
      label: "Talk to a Development Pro",
      to: "/contact/",
    },
    trustBar: "ONLINE, 30-MINUTES SESSIONS. Works around jobsite hours and travel.",
    symptomsEyebrow: "In the field",
    symptomsTitle: "What the schedule doesn't show you",
    symptoms: [
      {
        title: "Foremen and PMs blame each other",
        body: "Field blames office, office blames field.",
      },
      {
        title: "Nobody manages the sub, only the schedule",
        body: "Your foreman knows the sub is behind. He says nothing.",
      },
      {
        title: "Young engineers leave for a competitor",
        body: "Two years of drafting. Nobody told them what's next.",
      },
      {
        title: "Safety talk is a tailgate ritual",
        body: "Five minutes off a card. Nobody names the hazard.",
      },
    ],
    impactTitle: "What it costs on a job",
    impactBullets: [
      "Rework nobody wants to admit",
      "Schedule slip that started as conversation",
      "Journeymen leaving mid-project for another contractor",
      "Change orders born from missed communication",
    ],
    evidence: {
      quote:
        "Construction recorded 3.966 million separations in 2025 on roughly 8.3 million jobs, meaning the industry turns over close to half its workforce every year.",
      source: "U.S. Bureau of Labor Statistics, JOLTS Table 19, 2025 annual data",
      href: "https://www.bls.gov/news.release/jolts.t19.htm",
    },
    approachEyebrow: "How it works",
    approachTitle: "Training that fits jobsite hours",
    approachSteps: [
      {
        title: "Find the gaps",
        body: "A short assessment shows who needs what.",
      },
      {
        title: "30-minutes a week",
        body: "Completed anywhere.",
      },
      {
        title: "Practice with real challenges",
        body: "No theory without application here.",
      },
      {
        title: "Same language, field and office",
        body: "PMs and superintendents take the same courses.",
      },
    ],
    behaviorsTitle: "What we work on",
    behaviors: [
      {
        name: "Raising a problem early",
        body: "Flag the slip while there's still float.",
        courses: "Conquering Conflict",
      },
      {
        name: "Field-to-office communication",
        body: "Foremen and PMs stop talking past each other.",
        courses: "Strengthening Teams Through Communication",
      },
      {
        name: "Developing the next foreman",
        body: "Pick the journeyman who could run a crew.",
        courses: "Developing Others: Your Top Priority",
      },
      {
        name: "Making it safe to speak up",
        body: "Crews name the hazard instead of dodging it.",
        courses: "Psychological Safety",
      },
    ],
    finalTitle: "Start with one crew",
    finalBody:
      "Tell us where field and office keep colliding. We'll suggest one course and a group of foremen.",
    caseStudy: {
      label: "Illustrative scenario",
      company: "A 300-person commercial general contractor",
      situation:
        "Three projects, nine foremen, most promoted journeymen. Field and office argued through every RFI, and two foremen had already left.",
      action:
        "Foremen and PMs took Strengthening Teams Through Communication together, then Conquering Conflict. Superintendents added Developing Others.",
      result: [
        "RFIs raised days earlier in the field",
        "Fewer field-office arguments landing on the super",
        "Two journeymen ready to run their own crews",
      ],
    },
  },
  {
    slug: "professional-business-services",
    videoEmbedUrl: "https://play.gumlet.io/embed/6ad30ee2c56c0515acab8904",
    title: "Leadership Training for Professional Services Firms | Leadership Edge Live",
    metaDescription:
      "Manager training for accounting, consulting, legal, and agency firms. Online courses that fit around billable work and keep second-year staff from walking.",
    eyebrow: "PROFESSIONAL AND BUSINESS SERVICES",
    headline: "Your best biller is now managing eight people",
    supporting: "Nobody gave them time to learn how. Coaching is an afterthought.",
    primaryCTA: {
      label: "View the Course Catalog",
      to: "/course-catalog/",
    },
    secondaryCTA: {
      label: "Talk to a Development Pro",
      to: "/contact/",
    },
    trustBar: "ONLINE, 30-MINUTES SESSIONS. Fits between client work and billable targets.",
    symptomsEyebrow: "Inside the firm",
    symptomsTitle: "What partners notice too late",
    symptoms: [
      {
        title: "Managers coach only at review time",
        body: "Twelve months of silence, then a rating that surprises everyone.",
      },
      {
        title: "Utilization crowds out development",
        body: "Nobody gets credit for building a senior associate.",
      },
      {
        title: "The star performer is a liability",
        body: "They bill the most and burn through analysts.",
      },
      {
        title: "Second-year staff quit quietly",
        body: "No complaints. They took a call from a competitor.",
      },
    ],
    impactTitle: "What attrition costs a firm",
    impactBullets: [
      "Recruiting to replace people you trained",
      "Client relationships leaving with a manager",
      "Work gets redone by senior members of the team and no one learns",
      "Partners doing manager work again",
    ],
    evidence: {
      quote:
        "Professional and business services recorded 12.397 million separations in 2025 and carried a 5.3% job openings rate, the highest of any major private sector outside health care and hospitality.",
      source: "U.S. Bureau of Labor Statistics, JOLTS Tables 16 and 19, 2025 annual data",
      href: "https://www.bls.gov/news.release/jolts.t19.htm",
    },
    approachEyebrow: "How it works",
    approachTitle: "Development that survives a busy season",
    approachSteps: [
      {
        title: "Find the gaps",
        body: "A short assessment shows who needs what.",
      },
      {
        title: "30-minutes; once per week",
        body: "Short online sessions that fit around client deadlines.",
      },
      {
        title: "Practice with real challenges",
        body: "Solve tomorrow's problems today.",
      },
      {
        title: "Make it count",
        body: "Put development on the review next to utilization.",
      },
    ],
    behaviorsTitle: "What we work on",
    behaviors: [
      {
        name: "Feedback between reviews",
        body: "Say it in March, not at year end.",
        courses: "On-the-Spot Development Conversations",
      },
      {
        name: "Delegating real work",
        body: "Hand over the client work instead of doing it.",
        courses: "Delegating for Development",
      },
      {
        name: "Managing the difficult star",
        body: "Address the behavior without losing the book.",
        courses: "Conquering Conflict, Emotional Triggers",
      },
      {
        name: "Building people on purpose",
        body: "Growing an associate is the job, not extra.",
        courses: "The Mentoring Mindset",
      },
    ],
    finalTitle: "Start with one manager cohort",
    finalBody:
      "Tell us where you're losing second- and third-year staff. We'll suggest one course and a cohort.",
    caseStudy: {
      label: "Illustrative scenario",
      company: "A 120-person accounting and advisory firm",
      situation:
        "Managers were promoted on billings. Associates left every spring after busy season, and partners were pulled back into review work.",
      action:
        "Sixteen managers took Delegating for Development, then On-the-Spot Development Conversations. Partners added The Mentoring Mindset and put development on review criteria.",
      result: [
        "Feedback happening during the year, not after",
        "More second-year associates staying past busy season",
        "Partners keeping review work handed off",
      ],
    },
  },
  {
    slug: "mining-natural-resources",
    videoEmbedUrl: "https://play.gumlet.io/embed/6ad30ee2a55cdea9c48012e9",
    videoEmbedUrlMobile: "https://play.gumlet.io/embed/6ad31469a55cdea9c480284c",
    title: "Leadership Training for Mining and Natural Resources | Leadership Edge Live",
    metaDescription:
      "Supervisor training for mines, quarries, and camps. Online courses that fit rotations and build shift bosses before the retirement wave takes your bench.",
    eyebrow: "MINING AND NATURAL RESOURCES",
    headline: "Half your crew is leaving. Only some of them are retiring.",
    supporting:
      "Retirement, a competitor's operation, a better property. The knowledge leaves the same way regardless, and the network it ran on leaves with it.",
    primaryCTA: {
      label: "View the Course Catalog",
      to: "/course-catalog/",
    },
    secondaryCTA: {
      label: "Talk to a Development Pro",
      to: "/contact/",
    },
    trustBar: "ONLINE, 30-MINUTES SESSIONS. WORKS FOR ALL SHIFTS AND SITES.",
    symptomsEyebrow: "At the site",
    symptomsTitle: "What the great crew change looks like",
    symptoms: [
      {
        title: "Supervisors lead the way they were led",
        body: "Command and control worked when nobody had options. Not anymore.",
      },
      {
        title: "Nobody stops the job",
        body: "A crew sees something wrong and works around it.",
      },
      {
        title: "The knowledge leaves before anyone writes it down",
        body: "Thirty years of judgment about this ground, these crews, and these customers. It was never in a document, and it isn't transferring to anyone.",
      },
      {
        title: "You lose what they knew, and who they knew",
        body: "Mining runs on internal networks. When a long-tenured person walks, the relationships that actually moved work through this operation walk with them.",
      },
      {
        title: "Different crews; different results",
        body: "Inconsistent results from the different crews even though they face the same challenges.",
      },
    ],
    impactTitle: "What that costs a site",
    impactBullets: [
      "Safety incidents someone saw coming",
      "Safety and production challenges while a new supervisor learns",
      "Experienced operators leave due to leadership struggles",
      "No bench when shift bosses retire",
    ],
    evidence: {
      quote:
        "More than half of the current U.S. mining workforce will be retired and replaced by 2029, and only 162 bachelor's degrees in mining engineering were awarded in 2023.",
      source: "Society for Mining, Metallurgy & Exploration, 2025, via CEWD",
      href: "https://cewd.org/resources/energy-workforce-fast-facts/",
    },
    approachEyebrow: "How it works",
    approachTitle: "Built for different shifts and remote sites",
    approachSteps: [
      {
        title: "Find the gaps",
        body: "A short assessment shows who needs what.",
      },
      {
        title: "30-minutes a week",
        body: "No full day leadership training sessions.",
      },
      {
        title: "Practice on real challenges",
        body: "Practice the needed skills with the crew in the field.",
      },
      {
        title: "Build the bench early",
        body: "Develop the operators who could take a crew.",
      },
    ],
    behaviorsTitle: "What we work on",
    behaviors: [
      {
        name: "Making it safe to stop work",
        body: "Crews raise the hazard because it's safe to.",
        courses: "Psychological Safety",
      },
      {
        name: "Coaching instead of barking",
        body: "Same standard, different delivery, and something actually changes.",
        courses: "Collaborative Coaching Conversations",
      },
      {
        name: "Passing on what you know",
        body: "Senior operators teach judgment instead of keeping it.",
        courses: "The Mentoring Mindset",
      },
      {
        name: "Handling conflict on a crew",
        body: "Two operators, one grudge, sorted before shift end.",
        courses: "Conquering Conflict",
      },
    ],
    finalTitle: "Start with one crew",
    finalBody: "Tell us what leadership needs to look like at your sites in the next five years.",
    caseStudy: {
      label: "Illustrative scenario",
      company: "A 500-person aggregates and quarry operator",
      situation:
        "Four sites, two-week rotations, eleven shift bosses near retirement. The operators next in line had never led anyone.",
      action:
        "Twenty-two candidates ran New Leader Boot Camp on their off-swing, then Psychological Safety. Shift bosses took The Mentoring Mindset.",
      result: [
        "A named successor for every shift boss",
        "More stop-work calls raised before an incident",
        "Handover conversations starting months before retirement",
      ],
    },
  },
  {
    slug: "utilities-energy",
    videoEmbedUrl: "https://play.gumlet.io/embed/6ad30ee2c56c0515acab8902",
    videoEmbedUrlMobile: "https://play.gumlet.io/embed/6ad31469a55cdea9c480284d",
    title: "Leadership Training for Utilities and Energy Operators | Leadership Edge Live",
    metaDescription:
      "Crew leader training for utilities and energy. Online courses that fit on-call rotations and build leaders who can brief, coach, and hold a crew.",
    eyebrow: "UTILITIES AND ENERGY",
    headline: "Storm response shows who can actually lead",
    supporting: "Blue-sky days hide a lot. Storm nights show who can actually lead.",
    primaryCTA: {
      label: "View the Course Catalog",
      to: "/course-catalog/",
    },
    secondaryCTA: {
      label: "Talk to a Development Pro",
      to: "/contact/",
    },
    trustBar: "ONLINE, 30-MINUTES SESSIONS. Fits around on-call rotations and storm season.",
    symptomsEyebrow: "In the field",
    symptomsTitle: "What blue-sky days hide",
    symptoms: [
      {
        title: "Crew leaders were the best linemen",
        body: "Promoted for skill in a bucket, not for leading.",
      },
      {
        title: "Mutual aid exposes the gaps",
        body: "Crews arrive, your leader can't brief clearly. Restoration slows.",
      },
      {
        title: "Nobody has the safety conversation",
        body: "A crew skips a step. Nobody names it.",
      },
      {
        title: "The bench isn't there",
        body: "A quarter of your journeymen could retire tomorrow.",
      },
    ],
    impactTitle: "What it costs in the field",
    impactBullets: [
      "Longer restoration when crews aren't coordinated",
      "Safety events with warnings nobody raised",
      "Apprentices leaving before they turn out",
      "Reliability metrics that regulators watch",
    ],
    evidence: {
      quote:
        "76% of energy and utilities employers report a talent and skills gap within their existing workforce, versus 70% of U.S. employers overall.",
      source: "ManpowerGroup Talent Shortage, via Center for Energy Workforce Development, 2025",
      href: "https://cewd.org/resources/energy-workforce-fast-facts/",
    },
    approachEyebrow: "How it works",
    approachTitle: "Training that fits on-call schedules",
    approachSteps: [
      {
        title: "Find the gaps",
        body: "A short assessment shows who needs what.",
      },
      {
        title: "30-minutes a week",
        body: "Live, powerful micro-sessions are worth every minute.",
      },
      {
        title: "Practice before the storm",
        body: "Run it in shoulder season, before weather hits.",
      },
      {
        title: "Develop the next crew leader",
        body: "Build apprentices into leaders before the retirement wave.",
      },
    ],
    behaviorsTitle: "What we work on",
    behaviors: [
      {
        name: "Briefing under pressure",
        body: "Say it once so mutual aid crews follow.",
        courses: "Strengthening Teams Through Communication",
      },
      {
        name: "Stopping the shortcut",
        body: "Name the skipped step in the moment.",
        courses: "On-the-Spot Development Conversations",
      },
      {
        name: "Keeping people through change",
        body: "New tech, new rules. Crews hear why.",
        courses: "Leading Change",
      },
      {
        name: "Growing apprentices",
        body: "Journeymen teach so the trade doesn't retire.",
        courses: "The Mentoring Mindset",
      },
    ],
    finalTitle: "Start before storm season",
    finalBody:
      "Tell us how many crew leaders you'll need in five years. We'll suggest one course and a start date.",
    caseStudy: {
      label: "Illustrative scenario",
      company: "A municipal electric utility with 600 employees",
      situation:
        "Nineteen crew leaders, most promoted out of the bucket. Two storms showed the weak point was briefing mutual aid crews, not the line work.",
      action:
        "Crew leaders ran Strengthening Teams Through Communication, then Psychological Safety. Supervisors added Leading Change before a new rollout.",
      result: [
        "Cleaner briefings when mutual aid crews arrive",
        "More near misses reported, fewer found later",
        "Apprentices staying through turnout",
      ],
    },
  },
  {
    slug: "government-public-sector",
    videoEmbedUrl: "https://play.gumlet.io/embed/6ad30ee2a55cdea9c48012ea",
    title: "Leadership Training for Government and Public Sector Agencies | Leadership Edge Live",
    metaDescription:
      "Supervisor training for federal, state, and local agencies. Online courses priced and structured to clear procurement. No travel authorization needed.",
    eyebrow: "GOVERNMENT AND PUBLIC SECTOR",
    headline: "You can't pay more, so leadership is the cost",
    supporting: "The grade is fixed. What keeps a good analyst is a decent supervisor.",
    primaryCTA: {
      label: "View the Course Catalog",
      to: "/course-catalog/",
    },
    secondaryCTA: {
      label: "Talk to a Development Pro",
      to: "/contact/",
    },
    trustBar: "ONLINE, 30-MINUTES SESSIONS. Structured to fit a procurement cycle.",
    symptomsEyebrow: "Inside the agency",
    symptomsTitle: "What tenure and grades hide",
    symptoms: [
      {
        title: "Supervisors promoted on technical grade",
        body: "Best inspector becomes supervisor. No training, eleven direct reports.",
      },
      {
        title: "Performance conversations happen once a year",
        body: "The annual rating is the only feedback anyone gets.",
      },
      {
        title: "The problem employee stays a problem",
        body: "Documentation feels harder than tolerating it, so years pass.",
      },
      {
        title: "Training money expires unspent",
        body: "The line item exists. Nobody had time to scope it.",
      },
    ],
    impactTitle: "What it costs an agency",
    impactBullets: [
      "Vacancies you can't fill",
      "Institutional knowledge leaving with every retirement",
      "Grievances that started as conversations",
      "Good staff leaving for private sector",
    ],
    evidence: {
      quote:
        "Federal separations jumped to 624,000 in 2025 from 383,000 in 2024, a 63% increase in one year, while state and local government outside education ran a 4.2% openings rate.",
      source: "U.S. Bureau of Labor Statistics, JOLTS Tables 16 and 19, 2025 annual data",
      href: "https://www.bls.gov/news.release/jolts.t19.htm",
    },
    approachEyebrow: "How it works",
    approachTitle: "Structured to clear procurement quickly",
    approachSteps: [
      {
        title: "Find the gaps",
        body: "A short assessment shows who needs what.",
      },
      {
        title: "30-minutes; once per week",
        body: "Courses staff take at their desks. No travel authorization.",
      },
      {
        title: "Practice real cases",
        body: "Supervisors bring a real case, leave with next steps.",
      },
      {
        title: "Scale by division",
        body: "Start with one bureau, add cohorts as budget allows.",
      },
    ],
    behaviorsTitle: "What we work on",
    behaviors: [
      {
        name: "Feedback that isn't the annual rating",
        body: "Short, specific, and months before anything gets formal.",
        courses: "On-the-Spot Development Conversations, Performance Management",
      },
      {
        name: "Addressing performance early",
        body: "Handle it at step one, not year three.",
        courses: "Conquering Conflict",
      },
      {
        name: "Leading through reorganization",
        body: "Reductions, reassignments, new mandates. Staff hear it first.",
        courses: "Leading Change",
      },
      {
        name: "Keeping people you can't outbid",
        body: "Pay is fixed. The reason to stay isn't.",
        courses: "Building Employee Engagement",
      },
    ],
    finalTitle: "Start with one bureau",
    finalBody:
      "Tell us your fiscal calendar and where supervisors struggle. We'll scope one course and one cohort.",
    caseStudy: {
      label: "Illustrative scenario",
      company: "A county public works department with 700 employees",
      situation:
        "Supervisors were promoted off the crews on technical merit. Grievances climbed, two divisions had year-old vacancies, and ratings were the only feedback.",
      action:
        "Thirty supervisors across three divisions completed New Leader Boot Camp, then Performance Management. Division managers added Leading Change.",
      result: [
        "Performance issues addressed before they become grievances",
        "Feedback happening more than once a year",
        "Fewer transfers out of the hardest divisions",
      ],
    },
  },
];

/** Display names for the industry pages, keyed by slug. */
export const INDUSTRY_NAMES: Record<string, string> = {
  "small-midsize-businesses": "Small and Midsize Businesses",
  manufacturing: "Manufacturing",
  "supply-chain-logistics": "Supply Chain and Logistics",
  healthcare: "Healthcare",
  "construction-engineering": "Construction and Engineering",
  "professional-business-services": "Professional and Business Services",
  "mining-natural-resources": "Mining and Natural Resources",
  "utilities-energy": "Utilities and Energy",
  "government-public-sector": "Government and Public Sector",
};
